import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
 MapPin,
 Lock,
 ArrowRight,
 Plus,
 Building2,
 ExternalLink,
 CheckCircle2,
 Eye,
 MapPinned,
 CircleOff,
 Layers,
 ListChecks,
 Loader2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorSubscriptionFeatures } from '../../services/subscriptionService';
import { useToastContext } from '../../contexts/ToastContext';
import {
 useVendorLocations,
 useCreateVendorLocation,
 useUpdateVendorLocation,
 useDeleteVendorLocation,
 useToggleVendorLocationStatus,
} from '../../services/vendorLocationService';
import LocationCard from '../../components/vendor/locations/LocationCard';
import LocationFormModal from '../../components/vendor/locations/LocationFormModal';
import VendorServiceAreas from '../../components/client/VendorServiceAreas';
import Button from '../../components/shared/Button';
import StatsGrid from '../../components/shared/StatsGrid';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorMultiLocation = () => {
 const { user } = useAuth();
 const navigate = useNavigate();
 const { success: showSuccess, error: showError } = useToastContext();
 const { hasActiveSubscription, isChecking, hasFeature, isLoading: featureLoading } =
 useVendorSubscriptionFeatures('feature_multi_location');
 const [modalOpen, setModalOpen] = useState(false);
 const [editingLocation, setEditingLocation] = useState(null);
 const [deletingId, setDeletingId] = useState(null);
 const [togglingId, setTogglingId] = useState(null);

 const { data: locationsResponse, isLoading: locationsLoading } = useVendorLocations({
 enabled: hasActiveSubscription === true && hasFeature,
 });

 const createMutation = useCreateVendorLocation({
 onSuccess: () => {
 showSuccess('Location added', 'Your new service area is saved.');
 closeModal();
 },
 onError: (err) => {
 showError('Could not add location', err?.response?.data?.message || 'Please try again.');
 },
 });

 const updateMutation = useUpdateVendorLocation({
 onSuccess: () => {
 showSuccess('Location updated', 'Changes saved successfully.');
 closeModal();
 },
 onError: (err) => {
 showError('Could not update location', err?.response?.data?.message || 'Please try again.');
 },
 });

 const deleteMutation = useDeleteVendorLocation({
 onSuccess: () => showSuccess('Location removed', 'Service area deleted.'),
 onError: (err) => {
 showError('Could not remove location', err?.response?.data?.message || 'Please try again.');
 },
 });

 const toggleMutation = useToggleVendorLocationStatus({
 onSuccess: (res) => {
 showSuccess('Status updated', res?.message || 'Location status changed.');
 },
 onError: (err) => {
 showError('Could not update status', err?.response?.data?.message || 'Please try again.');
 },
 });

 useEffect(() => {
 if (user?.user_type !== 'vendor') {
 navigate('/');
 return;
 }
 if (hasActiveSubscription === false) {
 navigate('/vendor/subscription', { replace: true });
 }
 }, [user?.user_type, hasActiveSubscription, navigate]);

 const pageData = locationsResponse?.data || {};
 const locations = pageData.locations || [];
 const primary = pageData.primary_location || {};
 const stats = pageData.statistics || {};
 const maxLocations = pageData.max_locations || 25;
 const canAddMore = (stats.remaining ?? maxLocations) > 0;
 const activeLocations = locations.filter((location) => location.is_active);

 const closeModal = () => {
 setModalOpen(false);
 setEditingLocation(null);
 };

 const openCreate = () => {
 setEditingLocation(null);
 setModalOpen(true);
 };

 const openEdit = (location) => {
 setEditingLocation(location);
 setModalOpen(true);
 };

 const handleSubmit = (payload) => {
 if (editingLocation) {
 updateMutation.mutate({ id: editingLocation.id, data: payload });
 } else {
 createMutation.mutate(payload);
 }
 };

 const handleDelete = (location) => {
 if (!window.confirm(`Remove "${location.area_name}" from your service areas?`)) return;
 setDeletingId(location.id);
 deleteMutation.mutate(location.id, { onSettled: () => setDeletingId(null) });
 };

 const handleToggle = (location) => {
 setTogglingId(location.id);
 toggleMutation.mutate(location.id, { onSettled: () => setTogglingId(null) });
 };

 const primaryLabel = [primary.city, primary.state, primary.country].filter(Boolean).join(', ');
 const publicProfileUrl = pageData.profile_id ? `/vendor/${pageData.profile_id}` : null;

 const statsCards = [
 {
 key: 'total',
 title: 'Total areas',
 value: stats.total ?? 0,
 icon: Layers,
 variant: 'default',
 },
 {
 key: 'active',
 title: 'Active on profile',
 value: stats.active ?? 0,
 icon: CheckCircle2,
 variant: 'success',
 },
 {
 key: 'inactive',
 title: 'Inactive',
 value: stats.inactive ?? 0,
 icon: CircleOff,
 variant: stats.inactive > 0 ? 'warning' : 'default',
 },
 {
 key: 'remaining',
 title: 'Slots remaining',
 value: stats.remaining ?? maxLocations,
 icon: MapPinned,
 variant: canAddMore ? 'info' : 'warning',
 },
 ];

 if (isChecking || featureLoading || (hasFeature && locationsLoading) || hasActiveSubscription !== true) {
 return (
 <div className="min-h-screen bg-canvas flex items-center justify-center">
 <div className="text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading locations…</p>
 </div>
 </div>
 );
 }

 if (!hasFeature) {
 return (
 <div className="min-h-screen bg-canvas py-12 flex items-center justify-center px-4">
 <div className="max-w-md w-full product-panel p-8 text-center">
 <div className="w-16 h-16 bg-amber-50 border border-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
 <Lock className="w-8 h-8 text-amber-600" />
 </div>
 <h1 className="text-xl font-bold text-ink mb-2">Multi-Location</h1>
 <p className="text-ink-secondary mb-6">
 This feature is not included in your current plan. Upgrade to manage multiple service locations and reach clients across different areas.
 </p>
 <Link
 to="/vendor/subscription"
 className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink text-white rounded-md font-medium hover:bg-black transition-colors"
 >
 View Plans
 <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <div className="max-w-6xl mx-auto space-y-5">
 <VendorPageHeader
 title="Multi-Location"
 subtitle="Show clients everywhere you operate — on your public vendor profile."
 actions={
 <>
 {publicProfileUrl && (
 <Link to={publicProfileUrl} target="_blank" rel="noopener noreferrer">
 <Button type="button" variant="outline" size="sm" icon={ExternalLink} className="rounded-md">
 View public page
 </Button>
 </Link>
 )}
 <Button
 type="button"
 size="sm"
 icon={Plus}
 onClick={openCreate}
 disabled={!canAddMore}
 className="rounded-md"
 >
 Add location
 </Button>
 </>
 }
 />

 {stats.active > 0 && (
 <p className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 -mt-2">
 <CheckCircle2 className="w-4 h-4" />
 {stats.active} active service {stats.active === 1 ? 'area' : 'areas'} live on your profile
 </p>
 )}

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.multiLocation.title}
 steps={VENDOR_PAGE_GUIDES.multiLocation.steps}
 icon={ListChecks}
 />

 <StatsGrid stats={statsCards} columns={4} />

 <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
 <div className="space-y-5">
 <section className="product-panel p-5 sm:p-6">
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-xl border border-border bg-surface-muted flex items-center justify-center flex-shrink-0">
 <Building2 className="w-5 h-5 text-ink-secondary" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex flex-wrap items-center gap-2 mb-1">
 <h2 className="text-base font-semibold text-ink">Primary business location</h2>
 <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-surface-muted text-ink border border-border">
 HQ
 </span>
 </div>
 <p className="text-sm text-ink-secondary mb-2">
 This is your registered business address. It is separate from the extra areas you add below.
 </p>
 <p className="text-sm font-medium text-ink">{primary.business_name || 'Your business'}</p>
 {primaryLabel && <p className="text-sm text-ink-secondary mt-0.5">{primaryLabel}</p>}
 {primary.address && <p className="text-xs text-ink-tertiary mt-1">{primary.address}</p>}
 {primary.service_radius > 0 && (
 <p className="text-xs text-ink-tertiary mt-2">
 Default service radius: {primary.service_radius} miles from HQ
 </p>
 )}
 <Link
 to="/vendor/account?tab=business"
 className="inline-block text-xs text-ink hover:text-ink underline mt-2"
 >
 Edit in business profile
 </Link>
 </div>
 </div>
 </section>

 <section>
 <div className="flex items-center justify-between mb-4">
 <div>
 <h2 className="text-lg font-semibold text-ink">Additional service areas</h2>
 <p className="text-sm text-ink-secondary mt-0.5">
 {locations.length === 0
 ? 'Add areas beyond your HQ so nearby clients know you serve them.'
 : `${locations.length} of ${maxLocations} locations used`}
 </p>
 </div>
 {!canAddMore && (
 <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
 Maximum {maxLocations} locations reached
 </span>
 )}
 </div>

 {locations.length === 0 ? (
 <div className="product-panel border-dashed p-8 text-center">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <MapPin className="w-7 h-7" />
 </div>
 <h3 className="text-base font-medium text-ink mb-1">No extra locations yet</h3>
 <p className="text-sm text-ink-secondary max-w-md mx-auto mb-4">
 Add neighborhoods, cities, or regions you serve beyond your primary address. Only active areas appear on your public profile.
 </p>
 <Button type="button" icon={Plus} onClick={openCreate} className="rounded-md">
 Add your first location
 </Button>
 </div>
 ) : (
 <div className="space-y-3">
 {locations.map((location) => (
 <LocationCard
 key={location.id}
 location={location}
 onEdit={openEdit}
 onDelete={handleDelete}
 onToggle={handleToggle}
 isToggling={togglingId === location.id}
 isDeleting={deletingId === location.id}
 />
 ))}
 </div>
 )}
 </section>
 </div>

 <aside className="xl:sticky xl:top-24 h-fit">
 <section className="product-panel overflow-hidden">
 <div className="px-4 py-3 border-b border-border bg-surface-muted">
 <div className="flex items-center gap-2">
 <Eye className="w-4 h-4 text-ink-secondary" />
 <h3 className="text-sm font-semibold text-ink">Client preview</h3>
 </div>
 <p className="text-xs text-ink-tertiary mt-1">
 How your service areas appear on your public profile
 </p>
 </div>

 <div className="p-4 space-y-4">
 <div className="rounded-xl border border-border bg-surface-muted/80 p-3">
 <p className="text-[10px] uppercase tracking-wide text-ink-tertiary mb-1">Primary location</p>
 <p className="text-sm font-medium text-ink">{primary.business_name || 'Your business'}</p>
 <p className="text-xs text-ink-secondary mt-0.5">{primaryLabel || 'Set your address in business profile'}</p>
 </div>

 <div>
 <p className="text-sm font-medium text-ink mb-2">Service areas</p>
 <VendorServiceAreas areas={activeLocations} preview />
 </div>

 {publicProfileUrl && (
 <Link
 to={publicProfileUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center gap-1.5 text-xs text-ink hover:text-ink underline"
 >
 Open full public profile
 <ExternalLink className="w-3 h-3" />
 </Link>
 )}
 </div>
 </section>
 </aside>
 </div>
 </div>

 <LocationFormModal
 isOpen={modalOpen}
 onClose={closeModal}
 onSubmit={handleSubmit}
 initialLocation={editingLocation}
 isSaving={createMutation.isPending || updateMutation.isPending}
 primaryLocation={primary}
 />
 </div>
 );
};

export default VendorMultiLocation;
